import type { PublicKeyCredentialCreationOptionsJSON, PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';
import { getDeviceId, getStoredSession } from './session';

/** Base URL of the accounts Worker API — configure via VITE_ACCOUNTS_API_URL at build time. */
export const ACCOUNTS_API_URL: string = (import.meta.env.VITE_ACCOUNTS_API_URL as string | undefined) || '';

export class AccountsApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, options: { method?: string; body?: unknown; auth?: boolean } = {}): Promise<T> {
  if (!ACCOUNTS_API_URL) {
    throw new AccountsApiError('Accounts API is not configured (VITE_ACCOUNTS_API_URL is unset)', 0);
  }
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (options.auth) {
    const session = getStoredSession();
    if (!session) throw new AccountsApiError('Not signed in', 401);
    headers.Authorization = `Bearer ${session.sessionToken}`;
  }
  const res = await fetch(`${ACCOUNTS_API_URL}${path}`, {
    method: options.method ?? (options.body ? 'POST' : 'GET'),
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new AccountsApiError((json as { error?: string }).error ?? `Request failed (${res.status})`, res.status);
  }
  return json as T;
}

// ---------- Registration (signup) ----------

export function registerOptions(email: string) {
  return request<{ options: PublicKeyCredentialCreationOptionsJSON; challengeId: string }>('/register/options', {
    body: { email },
  });
}

export function registerVerify(body: {
  challengeId: string;
  email: string;
  deviceId: string;
  response: unknown;
  wrappedMasterKey?: string;
  wrapIv?: string;
  label?: string;
}) {
  return request<{ sessionToken: string; deviceTrustToken: string; userId: string; trustWindowDays: number }>(
    '/register/verify',
    { body },
  );
}

// ---------- Passkey login ----------

export function loginOptions(email?: string) {
  return request<{ options: PublicKeyCredentialRequestOptionsJSON; challengeId: string }>('/login/options', {
    body: { email },
  });
}

export function loginVerify(body: { challengeId: string; deviceId: string; response: unknown; label?: string }) {
  return request<{
    sessionToken: string;
    deviceTrustToken: string;
    userId: string;
    trustWindowDays: number;
    // null when this passkey's authenticator couldn't do the PRF extension — nothing was wrapped for it.
    wrappedKey: { credentialId: string; wrappedMasterKey: string; wrapIv: string } | null;
  }>('/login/verify', { body });
}

// ---------- OTP login (trusted devices) ----------

export function otpRequest(email: string, deviceId: string) {
  return request<{ ok: true }>('/otp/request', { body: { email, deviceId } });
}

export function otpVerify(email: string, deviceId: string, code: string) {
  return request<{ sessionToken: string; userId: string }>('/otp/verify', { body: { email, deviceId, code } });
}

// ---------- Account ----------

export function accountMe() {
  return request<{ userId: string; email: string; trustWindowDays: number }>('/account/me', { auth: true });
}

export function patchTrustWindow(days: 1 | 7 | 30) {
  return request<{ ok: true; trustWindowDays: number }>('/account/trust-window', {
    method: 'PATCH',
    auth: true,
    body: { days },
  });
}

export function getWrappedKeys() {
  return request<{ wrappedKeys: { credentialId: string; wrappedMasterKey: string; wrapIv: string }[] }>(
    '/account/wrapped-keys',
    { auth: true, method: 'GET' },
  );
}

export function addPasskeyOptions() {
  return request<{ options: PublicKeyCredentialCreationOptionsJSON; challengeId: string }>(
    '/account/passkeys/options',
    { auth: true, method: 'POST', body: {} },
  );
}

export function addPasskeyVerify(body: {
  challengeId: string;
  response: unknown;
  wrappedMasterKey?: string;
  wrapIv?: string;
  label?: string;
}) {
  return request<{ ok: true; credentialId: string }>('/account/passkeys/verify', { auth: true, body });
}

// ---------- Sync ----------

export function getSyncSettings() {
  return request<{ data: string | null; updatedAt: number | null }>('/sync/settings', { auth: true, method: 'GET' });
}

export function putSyncSettings(data: unknown) {
  return request<{ ok: true }>('/sync/settings', { auth: true, method: 'PUT', body: { data } });
}

export function getSyncConversations() {
  return request<{ conversations: { id: string; data: string; updatedAt: number }[] }>('/sync/conversations', {
    auth: true,
    method: 'GET',
  });
}

export function putSyncConversation(id: string, data: unknown) {
  return request<{ ok: true }>(`/sync/conversations/${encodeURIComponent(id)}`, {
    auth: true,
    method: 'PUT',
    body: { data },
  });
}

export function deleteSyncConversation(id: string) {
  return request<{ ok: true }>(`/sync/conversations/${encodeURIComponent(id)}`, { auth: true, method: 'DELETE' });
}

export function getSyncApiKey() {
  return request<{ ciphertext: string | null; iv: string | null; updatedAt?: number }>('/sync/api-key', {
    auth: true,
    method: 'GET',
  });
}

export function putSyncApiKey(ciphertext: string, iv: string) {
  return request<{ ok: true }>('/sync/api-key', { auth: true, method: 'PUT', body: { ciphertext, iv } });
}

export function getSyncGithubToken() {
  return request<{ ciphertext: string | null; iv: string | null; updatedAt?: number }>('/sync/github-token', {
    auth: true,
    method: 'GET',
  });
}

export function putSyncGithubToken(ciphertext: string, iv: string) {
  return request<{ ok: true }>('/sync/github-token', { auth: true, method: 'PUT', body: { ciphertext, iv } });
}

// ---------- Benchmarks (per-user, per-device usage stats) ----------

export interface BenchmarkDeviceView {
  deviceId: string;
  label: string | null;
  requests: number;
  tokensBefore: number;
  tokensAfter: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  costUsd: number;
  updatedAt: number;
}

export interface BenchmarkTotals {
  requests: number;
  tokensBefore: number;
  tokensAfter: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  costUsd: number;
}

export function putBenchmarks(body: BenchmarkTotals & { label?: string }) {
  return request<{ ok: true }>('/benchmarks', { auth: true, method: 'PUT', body });
}

export function getBenchmarks() {
  return request<{ devices: BenchmarkDeviceView[]; totals: BenchmarkTotals }>('/benchmarks', {
    auth: true,
    method: 'GET',
  });
}

export { getDeviceId };
